// ============================================================================
// BREAKING NEWS OVERLAY - Full-bleed takeover for a tabletop broadcast-channel
// inject (plan 6.2.1). Follows CinematicCardOverlay's full-bleed pattern
// rather than a grid widget, but is reactive to state.tabletop.activeBroadcast
// rather than a discrete BroadcastChannel message — the backend's
// buildSecondScreenState already decides the active window (60s wall-clock
// from the triggering inject's delivery), so the frontend just renders while
// that field is non-null and disappears on its own once polling picks up the
// backend clearing it.
//
// Mounted unconditionally in SecondScreenPage.tsx (not gated on the
// 'cinematic-live' layout like CinematicCardOverlay) — a breaking-news
// bulletin should interrupt whatever layout is active.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';

const FADE_MS = 600;
const STING_LEAD_MS = 1500; // assumed sting duration before the still/VO starts — tune once a real sting asset exists

const BreakingNewsOverlay: React.FC = () => {
  const { state } = useSecondScreenData();
  const broadcast = state?.tabletop?.activeBroadcast ?? null;
  const [visible, setVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const stingRef = useRef<HTMLAudioElement | null>(null);
  const voRef = useRef<HTMLAudioElement | null>(null);
  const lastLowerThirdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!broadcast) {
      setVisible(false);
      setShowContent(false);
      lastLowerThirdRef.current = null;
      return;
    }
    // Only replay the sting sequence when a *new* bulletin arrives (the
    // lower-third text is the closest thing to a bulletin identity we have,
    // since injects don't carry a stable "broadcast instance" id).
    if (lastLowerThirdRef.current === broadcast.lowerThird) return;
    lastLowerThirdRef.current = broadcast.lowerThird;

    setVisible(true);
    setShowContent(false);
    // Sting is a static asset that may not exist yet (see progress doc) —
    // failing to play it should never block the still/VO from showing.
    stingRef.current?.play().catch(() => {});
    const timer = setTimeout(() => {
      setShowContent(true);
      voRef.current?.play().catch(() => {});
    }, STING_LEAD_MS);
    return () => clearTimeout(timer);
  }, [broadcast]);

  if (!visible || !broadcast) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black transition-opacity"
      style={{ opacity: showContent ? 1 : 0.4, transitionDuration: `${FADE_MS}ms` }}
    >
      <audio ref={stingRef} src={broadcast.stingUrl} />
      {broadcast.audioUrl && <audio ref={voRef} src={broadcast.audioUrl} />}

      {broadcast.stillUrl && (
        <img
          src={broadcast.stillUrl}
          alt="Breaking news"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: showContent ? 1 : 0 }}
        />
      )}

      {showContent && (
        <div className="relative w-full bg-gradient-to-t from-black via-black/80 to-transparent px-10 py-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-2 py-1 rounded">Breaking</span>
            <span className="text-white/50 text-xs uppercase tracking-widest">Live</span>
          </div>
          <p className="text-white text-2xl font-semibold leading-snug max-w-4xl">{broadcast.lowerThird}</p>
        </div>
      )}
    </div>
  );
};

export default BreakingNewsOverlay;
